//@flow
import React, {useState} from 'react';
import {
  CircularProgress,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {Publish} from '@material-ui/icons';
import {useForm} from 'react-hook-form';

import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {useCurrencies} from '../../../currency/utils';
import {getCurrencyInSpanish} from '../../../on-boarding/utils';

import {
  LOGO_PAYMENT_METHODS,
  DOCUMENT_TYPES,
  ADD_NEW_BANK,
} from '../utils/paymentData';
import {INPUT_FIELDS_BY_PAYMENT_METHOD} from '../utils/paymentFields';
import AddNewMethodModal from './AddNewMethodModal';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import type {LogoPaymentMethod} from '../utils/paymentData';
import type {PaymentMethod} from '../../../../entities';
import type {PaymentMethodAction, PaymentMethodRequest} from '../duck';
import {useStoreCountry} from '../../../country/utils';
import {getImageUrl} from '../../../../utils/image_provider';
import {colors} from '../../../../assets/styles/colors';

import defaultImage from '../../../../assets/img/image-default.jpg';
import {compressImage} from '../../../../utils/image';
type Props = {
  hasPmMethodDisabled: boolean,
  isSaving: boolean,
  paymentMethod?: PaymentMethod,
  addLogoPaymentMethod: (params: PaymentMethodAction) => void,
};

type FormData = {
  instructions?: string,
  documentNumber?: string,
  accountHolder?: string,
  accountNumber?: string,
  interbankNumber?: string,
  numberMethod?: string,
};

const useStyles = makeStyles(theme => ({
  saveButton: {
    margin: theme.spacing(2, 0),
    borderRadius: '32px',
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  bankOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '.85rem',

    '&[data-selected-option]': {
      padding: 0,
    },
  },
  bankPicture: {
    height: 40,
    maxWidth: 100,
    position: 'absolute',
    right: '1.5rem',
    top: '.5rem',
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  methodLabel: {
    fontSize: '14px',
    whiteSpace: 'normal',
    maxWidth: '19ch',
  },

  qrErrorLabel: {
    color: colors.error,
    fontSize: '12px',
  },

  qrInput: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '.25rem .75rem .25rem .25rem',
    borderRadius: '.5rem',
    border: `2px dashed ${colors.grey300}`,
    cursor: 'pointer',
    color: colors.purple,

    '&[data-disabled="true"]': {
      pointerEvents: 'none',
      opacity: 0.5,
    },

    '&[data-error="true"]': {
      borderColor: colors.error,
    },
  },
  qrImage: {
    height: 41,
    width: 41,
    borderRadius: '.25rem',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
    },
  },
  qrLabel: {
    // color: colors.purple,
  },
}));

export default function LogoPaymentMethodForm(props: Props) {
  const {
    hasPmMethodDisabled,
    isSaving,
    paymentMethod,
    addLogoPaymentMethod,
  } = props;

  //default data
  const clientParameters = paymentMethod && paymentMethod.clientParameters;
  const defaultCurrencyId = clientParameters && clientParameters.currency;
  const defaultDocumentType = clientParameters && clientParameters.documentType;
  const defaultQrImage =
    paymentMethod && paymentMethod.fileId ? paymentMethod.fileId : undefined;

  const country = useStoreCountry();

  const {handleSubmit, register, errors} = useForm<FormData>({
    defaultValues: {
      instructions: clientParameters && clientParameters.description,
      documentNumber: clientParameters && clientParameters.documentNumber,
      accountHolder: clientParameters && clientParameters.identifier,
      accountNumber: clientParameters && clientParameters.accountNumber,
      interbankNumber:
        clientParameters && clientParameters.internationalBankAccountNumber,
      numberMethod: clientParameters && clientParameters.number,
    },
  });

  const classes = useStyles();
  const currencies = useCurrencies();

  const [logoPaymentMethodList, setLogoPaymentMethodList] = useState<
    LogoPaymentMethod[]
  >(() => {
    if (!paymentMethod) return LOGO_PAYMENT_METHODS;
    const existMethod = LOGO_PAYMENT_METHODS.find(
      ({label, value}) =>
        paymentMethod.displayName === label ||
        paymentMethod.displayName === value
    );

    if (existMethod) return LOGO_PAYMENT_METHODS;

    const method = {
      label: paymentMethod.displayName,
      value: paymentMethod.displayName,
      logoId: paymentMethod.logoId,
      category: 'others',
    };

    return LOGO_PAYMENT_METHODS.concat(method);
  });

  const [qrFile, setQrFile] = useState<File | null>(null);
  const [compressingQr, setCompressingQr] = useState(false);
  const [qrError, setQrError] = useState(false);

  const getQrImage = () => {
    if (qrFile) {
      return URL.createObjectURL(qrFile);
    }

    if (defaultQrImage) {
      return getImageUrl(defaultQrImage);
    }

    return defaultImage;
  };

  const qrImage = getQrImage();

  const [currencyId, setCurrencyId] = useState(defaultCurrencyId);
  const [documentType, setDocumentType] = useState(defaultDocumentType);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [
    selectedLogoMethod,
    setSelectedLogoMethod,
  ] = useState<LogoPaymentMethod | null>(() => {
    if (!paymentMethod) return null;
    const result = LOGO_PAYMENT_METHODS.find(({value, label}) => {
      return (
        paymentMethod.displayName === value ||
        paymentMethod.displayName === label
      );
    });

    if (result) return result;

    const method = {
      label: paymentMethod.displayName,
      value: paymentMethod.displayName,
      logoId: paymentMethod.logoId,
      category: 'others',
    };

    return method;
  });

  const [methodModalOpen, setMethodModalOpen] = useState(false);

  const openMethodModal = () => {
    setMethodModalOpen(true);
  };

  const closeMethodModal = () => {
    setMethodModalOpen(false);
  };

  const handleBankChange = event => {
    const value = event.target.value;
    const method = logoPaymentMethodList.find(({value: v}) => v === value);
    if (method) {
      setSelectedLogoMethod(method);
    }

    if (value === ADD_NEW_BANK.value) {
      openMethodModal();
    }
  };

  const handleAddNewMethod = data => {
    const {logo, name, preview} = data;
    const newMethod: LogoPaymentMethod = {
      label: name,
      value: name,
      category: 'others',
      localPreview: preview,
    };

    if (logo) setLogoFile(logo);

    setLogoPaymentMethodList([...logoPaymentMethodList, newMethod]);
    setSelectedLogoMethod(newMethod);

    closeMethodModal();
  };

  const handleQrChange = async (file: File) => {
    const megabyte = 1024 * 1024;
    const originalSize = file.size / megabyte;

    if (originalSize < 1) {
      setQrFile(file);
      return;
    }

    const quality = 1 / originalSize;
    try {
      setQrError(false);
      setCompressingQr(true);
      const [, image] = await compressImage(undefined, file, quality);
      setQrFile(image);
    } catch (error) {
      setQrFile(null);
      setQrError(true);
    } finally {
      setCompressingQr(false);
    }
  };

  const methodCategorySelected =
    !!selectedLogoMethod && selectedLogoMethod.category;

  const methodFields = methodCategorySelected
    ? INPUT_FIELDS_BY_PAYMENT_METHOD[methodCategorySelected]
    : INPUT_FIELDS_BY_PAYMENT_METHOD.bank;

  const onSubmit = data => {
    if (!selectedLogoMethod) return;
    const commonClientParameters = {
      documentType: documentType || undefined,
      currency: currencyId || undefined,
      description: data.instructions || undefined,
      documentNumber: data.documentNumber || undefined,
      identifier: data.accountHolder || undefined,
    };

    const categoryMethod = selectedLogoMethod.category;

    const qrCodePaymentMethodRequest = qrFile ? qrFile : undefined;

    if (categoryMethod === 'others' || categoryMethod === 'bank') {
      const logoPaymentMethodRequest = logoFile ? logoFile : undefined;
      const logoId = !logoPaymentMethodRequest
        ? selectedLogoMethod.logoId || undefined
        : undefined;

      const paymentMethodRequest: PaymentMethodRequest = {
        logoId,
        enabled: true,
        id: paymentMethod ? paymentMethod.id : undefined,
        displayName: selectedLogoMethod.label,
        fileId: defaultQrImage,
        clientParameters: {
          ...commonClientParameters,
          accountNumber: data.accountNumber || undefined,
          internationalBankAccountNumber: data.interbankNumber || undefined,
        },
      };

      addLogoPaymentMethod({
        logoPaymentMethodRequest,
        paymentMethodRequest: paymentMethodRequest,
        qrCodePaymentMethodRequest,
      });
    }

    if (categoryMethod === 'digital_wallet') {
      const paymentMethodRequest: PaymentMethodRequest = {
        enabled: true,
        displayName: selectedLogoMethod.label,
        id: paymentMethod ? paymentMethod.id : undefined,
        logoId: selectedLogoMethod.logoId,
        fileId: defaultQrImage,
        clientParameters: {
          ...commonClientParameters,
          number: data.numberMethod,
        },
      };
      addLogoPaymentMethod({paymentMethodRequest, qrCodePaymentMethodRequest});
    }
    if (categoryMethod === 'cash_on_delivery') {
      const paymentMethodRequest: PaymentMethodRequest = {
        enabled: true,
        displayName: selectedLogoMethod.label,
        logoId: selectedLogoMethod.logoId,
        id: paymentMethod ? paymentMethod.id : undefined,
        fileId: defaultQrImage,
        clientParameters: {
          identifier: selectedLogoMethod.label,
          description: data.instructions || undefined,
        },
      };
      addLogoPaymentMethod({paymentMethodRequest, qrCodePaymentMethodRequest});
    }
  };

  return (
    <>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.formGrid}>
          <CustomSelect
            label="Banco"
            name="bank"
            renderValue={selected => {
              const bank = logoPaymentMethodList.find(
                ({value}) => value === selected
              );
              if (!bank) return <Typography>{selected}</Typography>;

              const {label, logoId, localPreview} = bank;

              return (
                <div className={classes.bankOption} data-selected-option>
                  <Typography>{label}</Typography>
                  <picture className={classes.bankPicture}>
                    {(logoId || localPreview) && (
                      <img
                        src={localPreview || getImageUrl(logoId)}
                        alt={label}
                      />
                    )}
                  </picture>
                </div>
              );
            }}
            onChange={handleBankChange}
            value={selectedLogoMethod && selectedLogoMethod.value}
            InputLabelProps={
              paymentMethod &&
              selectedLogoMethod && {
                shrink: true,
              }
            }
          >
            {logoPaymentMethodList
              .filter(paymentMethod => {
                if (!paymentMethod.country) return true;
                return country && paymentMethod.country === country.code;
              })
              .map(({logoId, label, value, localPreview, category}) => (
                <MenuItem
                  value={value}
                  key={value}
                  className={classes.bankOption}
                >
                  <Typography className={classes.methodLabel}>
                    {label}
                  </Typography>
                  {(logoId || localPreview) && (
                    <picture className={classes.bankPicture}>
                      <img
                        src={localPreview || getImageUrl(logoId)}
                        alt={label}
                      />
                    </picture>
                  )}
                </MenuItem>
              ))}
            <MenuItem value={ADD_NEW_BANK.value} className={classes.bankOption}>
              <Typography>{ADD_NEW_BANK.label}</Typography>
            </MenuItem>
          </CustomSelect>
          {methodFields.map(({label, required, type, value}) => {
            const isRequired =
              required &&
              (methodCategorySelected !== 'others' ||
                value === 'accountHolder');

            if (type === 'currency') {
              const inputLabel = isRequired ? 'Moneda' : 'Moneda (opcional)';

              return (
                <CustomSelect
                  key={value}
                  label={inputLabel}
                  disabled={!Array.isArray(currencies)}
                  required={isRequired}
                  name={value}
                  value={currencyId}
                  onChange={event => setCurrencyId(event.target.value)}
                  InputLabelProps={
                    paymentMethod &&
                    currencyId && {
                      shrink: true,
                    }
                  }
                >
                  {currencies &&
                    currencies.map(currency => (
                      <MenuItem value={currency.id} key={currency.id}>
                        {`${getCurrencyInSpanish(currency.code)} (${
                          currency.symbol
                        })`}
                      </MenuItem>
                    ))}
                </CustomSelect>
              );
            }

            if (type === 'document_type') {
              const inputLabel = isRequired
                ? 'Tipo de documento'
                : 'Tipo de documento (opcional)';
              return (
                <CustomSelect
                  key={value}
                  label={inputLabel}
                  required={isRequired}
                  name={value}
                  value={documentType}
                  onChange={event => setDocumentType(event.target.value)}
                  InputLabelProps={
                    paymentMethod &&
                    documentType && {
                      shrink: true,
                    }
                  }
                >
                  {DOCUMENT_TYPES.map(({label, value}) => (
                    <MenuItem value={value} key={value}>
                      {label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              );
            }

            const inputLabel = isRequired ? label : `${label} (opcional)`;

            return (
              <CustomTextField
                key={value}
                fullWidth
                label={inputLabel}
                required={isRequired}
                name={value}
                inputRef={register({
                  required: isRequired ? 'Campo obligatorio' : false,
                })}
                error={!!errors[value]}
                helperText={errors[value] && errors[value].message}
                InputLabelProps={
                  clientParameters && {
                    shrink: true,
                  }
                }
              />
            );
          })}

          <label
            className={classes.qrInput}
            data-disabled={compressingQr}
            data-error={qrError}
          >
            <picture className={classes.qrImage}>
              {compressingQr ? (
                <CircularProgress size={24} />
              ) : (
                <img src={qrImage} alt="Código Qr" />
              )}
            </picture>
            {!qrError && (
              <span className={classes.qrLabel}>
                {defaultQrImage
                  ? 'Cambiar código QR'
                  : 'Subir QR (Opcional) 250 x 250 px'}
              </span>
            )}

            {qrError && (
              <span className={classes.qrErrorLabel}>
                ocurrio un error, intente nuevamente
              </span>
            )}

            <Publish />

            <input
              type="file"
              hidden
              accept="image/*"
              onChange={event => {
                const file = event.target.files[0];
                if (file) {
                  handleQrChange(file);
                }
              }}
            />
          </label>
        </div>
        {!hasPmMethodDisabled && (
          <CustomButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            className={classes.saveButton}
            disabled={isSaving}
            id="save-payment-method-others"
          >
            {paymentMethod ? 'Guardar cambios' : 'Agregar'}
          </CustomButton>
        )}
      </form>
      <AddNewMethodModal
        open={methodModalOpen}
        handleClose={closeMethodModal}
        onSubmit={handleAddNewMethod}
      />
    </>
  );
}
